<template>
  <div
    :class="`formulate-input-element formulate-input-element--${context.class}`"
    :data-type="context.type"
  >
    <Dropdown
      :value="selectedSiteObj"
      :options="siteOptions"
      optionLabel="label"
      dataKey="id"
      :filter="siteOptions.length > 8"
      :placeholder="placeholder"
      :showClear="true"
      :disabled="!localVendorId || loadingSites || context.attributes.disabled"
      class="w-100"
      v-bind="context.attributes"
      @input="onSelect"
      @hide="context.blurHandler"
    >
      <!-- no optionValue="id" -- like FormulateVendorSelect, up/down arrows break with it, so we pass full option objects and store only the id -->
      <template #indicator>
        <span class="p-dropdown-trigger-icon pi pi-chevron-down"></span>
      </template>
      <template #value="slotProps">
        <div v-if="slotProps.value">
          <!-- {{ slotProps.value.label }} -->
          <div>{{ slotProps.value.name }}</div>
          <div
            v-if="slotProps.value.city"
            class="text-muted font-size-80"
          >
            {{ slotProps.value.city }}
          </div>
        </div>
        <span
          v-else-if="loadingSites"
          class="text-muted"
        >
          <b-spinner
            small
            class="text-muted mr-1"
          ></b-spinner>
          {{ $t('formulateVendorSitePicker.loading') }}
        </span>
        <span v-else>{{ slotProps.placeholder }}</span>
      </template>
      <template #option="slotProps">
        <div>{{ slotProps.option.name }}</div>
        <div
          v-if="slotProps.option.city"
          class="text-muted font-size-80"
        >
          {{ slotProps.option.city }}
        </div>
      </template>
    </Dropdown>
    <div
      v-if="localVendorId && !loadingSites && sites.length === 0"
      class="text-muted font-size-80 mt-1"
    >
      {{ $t('formulateVendorSitePicker.noSites') }}
    </div>
  </div>
</template>

<script>
  import { suppliosApi } from '@/js/api-service';
  import { errorHandling } from '@/js/custom-vue-mixins';
  import Dropdown from 'primevue/dropdown';

  // vue-formulate custom input ('vendorsitepicker') -- pick ONE CompanySite of the vendor chosen in another field on the SAME form
  // reads/writes context.model (the CompanySite ID); loads the vendor's sites itself, and clears the value when the vendor changes
  // ONLY for tenants with custom_flags.use_vendor_sites -- see FormulateVendorSitePicker in state_machines/flows/forms/formulate_fields.py
  export default {
    name: 'FormulateVendorSitePicker',
    components: {
      Dropdown,
    },
    mixins: [errorHandling],
    i18n: {
      messages: {
        en: {
          formulateVendorSitePicker: {
            selectVendorFirst: 'Select a supplier first',
            selectSite: 'Select a site...',
            loading: 'Loading...',
            noSites: 'This supplier has no sites yet.',
            errorLoading: 'Error while loading supplier sites',
          },
        },
        es: {
          formulateVendorSitePicker: {
            selectVendorFirst: 'Seleccione primero un proveedor',
            selectSite: 'Seleccione un sitio...',
            loading: 'Cargando...',
            noSites: 'Este proveedor aún no tiene sitios.',
            errorLoading: 'Error al cargar los sitios del proveedor',
          },
        },
        pt: {
          formulateVendorSitePicker: {
            selectVendorFirst: 'Selecione primeiro um fornecedor',
            selectSite: 'Selecione uma unidade...',
            loading: 'Carregando...',
            noSites: 'Este fornecedor ainda não tem unidades.',
            errorLoading: 'Erro ao carregar as unidades do fornecedor',
          },
        },
        'zh-Hans': {
          formulateVendorSitePicker: {
            selectVendorFirst: '请先选择供应商',
            selectSite: '选择站点...',
            loading: '加载中...',
            noSites: '该供应商暂无站点。',
            errorLoading: '加载供应商站点时出错',
          },
        },
        'zh-Hant': {
          formulateVendorSitePicker: {
            selectVendorFirst: '請先選擇供應商',
            selectSite: '選擇據點...',
            loading: '載入中...',
            noSites: '此供應商尚無據點。',
            errorLoading: '載入供應商據點時發生錯誤',
          },
        },
      },
    },
    props: {
      context: {
        type: Object,
        required: true,
      },
      vendorId: {  // optional prop; the actual vendor ID used is localVendorId
        type: String,
        required: false,
      },
      // vendorField -- name of the field on the SAME FORM that has the vendor ID as its value
      vendorField: {
        type: String,
        default: 'vendor',
      },
    },
    data() {
      return {
        formComponent: null, // the parent FormulateForm
        localVendorId: '',
        sites: [],
        loadingSites: false,
      };
    },
    computed: {
      siteOptions() {
        // option objects for the Dropdown -- 'label' is shown in the selected value, name/city in the option list
        return this.sites.map((site) => ({
          id: site.id,
          name: site.display_name,
          city: site.address?.formatted_city_state_country || '',
          label: this.siteLabel(site),
        }));
      },
      selectedSiteObj() {
        return this.siteOptions.find((option) => option.id === this.context.model) ?? null;
      },
      placeholder() {
        if (!this.localVendorId) {
          return this.$t('formulateVendorSitePicker.selectVendorFirst');
        }
        return this.context.attributes?.placeholder ?? this.$t('formulateVendorSitePicker.selectSite');
      },
    },
    mounted() {
      this.findFormComponent();
      this.localVendorId = this.vendorId || this.getVendorIdFromForm();
      this.loadSites();
    },
    beforeDestroy() {
      if (this.formComponent) {
        this.formComponent.$off('input', this.onFormInput);
      }
    },
    methods: {
      findFormComponent() {
        // climb the component tree to the parent FormulateForm (same approach as FormulateVendorContactPicker)
        let maybeParent = this.$parent;
        while (maybeParent) {
          if (maybeParent.$options.name === 'FormulateForm') {
            this.formComponent = maybeParent;
            this.formComponent.$on('input', this.onFormInput);
            return;
          }
          maybeParent = maybeParent.$parent;
        }
      },
      getVendorIdFromForm() {
        const fromData = this.formComponent?.proxy?.[this.vendorField];
        const fromInitial = this.formComponent?.initialValues?.[this.vendorField];
        return fromData ?? fromInitial ?? '';
      },
      onFormInput() {
        const newVendorId = this.vendorId || this.getVendorIdFromForm();  // from prop or form
        if (newVendorId === this.localVendorId) return;
        this.localVendorId = newVendorId;
        this.context.model = null; // a site from the old vendor is never valid for the new one
        this.loadSites();
      },
      async loadSites() {
        console.log('Loading sites for localVendorId:', this.localVendorId);
        this.sites = [];
        if (!this.localVendorId) return;
        this.loadingSites = true;
        try {
          const response = await suppliosApi.companySite.list({ params: { company: this.localVendorId, linkable: true } });  // only active sites can be linked (Oct 2026 site states)
          this.sites = response.data;
          // drop a stale value that is not one of this vendor's sites
          if (this.context.model && !this.sites.some((site) => site.id === this.context.model)) {
            this.context.model = null;
          }
        } catch (error) {
          this.errorToaster(error, this.$t('formulateVendorSitePicker.errorLoading'));
        } finally {
          this.loadingSites = false;
        }
      },
      siteLabel(site) {
        // probably no need to use this... we handle adding city to name if needed in backend
        const city = site.address?.formatted_city_state_country;
        return city && city !== site.display_name ? `${site.display_name} (${city})` : site.display_name;
      },
      onSelect(option) {
        // option is the full option object, or null when cleared
        this.context.model = option ? option.id : null;
      },
    },
  };
</script>
